import React, { useState, useEffect, useRef } from "react";

import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  CardHeader,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import defaultAvatar from "../../assets/images/users/avatar-1.jpg";
import {
  getUserStatus,
  updateUserProfile,
  changeUserPassword,
  changeUserAvatar,
} from "../../helpers/fakebackend_helper";

const UserProfile = () => {
  document.title = "Profile | ApiHub.today";

  const [currentUser, setCurrentUser] = useState(null);
  const [profileAlert, setProfileAlert] = useState(null); // { color, message }
  const [passwordAlert, setPasswordAlert] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const avatarInputRef = useRef(null);

  const loadUser = async () => {
    try {
      const res = await getUserStatus();
      if (res && res.success) setCurrentUser(res.data);
    } catch (e) { /* handled by axios interceptor */ }
  };

  useEffect(() => { loadUser(); }, []);

  const onAvatarSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setProfileAlert({ color: "danger", message: "Avatar must be an image." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setProfileAlert({ color: "danger", message: "Avatar too large. Max 5MB." });
      return;
    }
    setAvatarUploading(true);
    setProfileAlert(null);
    try {
      const res = await changeUserAvatar(file);
      if (res && res.success) {
        setProfileAlert({ color: "success", message: "Avatar updated." });
        loadUser();
      } else {
        setProfileAlert({ color: "danger", message: (res && res.message) || "Failed to update avatar." });
      }
    } catch (err) {
      setProfileAlert({ color: "danger", message: "Failed to update avatar." });
    } finally {
      setAvatarUploading(false);
    }
  };

  const profileValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: (currentUser && currentUser.first_name) || "",
      last_name: (currentUser && currentUser.last_name) || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().trim().required("Please Enter Your First Name"),
      last_name: Yup.string(),
    }),
    onSubmit: async (values) => {
      setProfileSaving(true);
      setProfileAlert(null);
      try {
        const res = await updateUserProfile(values.first_name.trim(), (values.last_name || "").trim());
        if (res && res.success) {
          setProfileAlert({ color: "success", message: "Profile updated." });
          loadUser();
        } else {
          setProfileAlert({ color: "danger", message: (res && res.message) || "Failed to update profile." });
        }
      } catch (err) {
        setProfileAlert({ color: "danger", message: "Failed to update profile." });
      } finally {
        setProfileSaving(false);
      }
    },
  });

  const hasPassword = !!(currentUser && currentUser.has_password);
  const passwordValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      current_password: hasPassword
        ? Yup.string().required("Please Enter Your Current Password")
        : Yup.string(),
      new_password: Yup.string().min(6, "Password must be at least 6 characters").required("Please Enter A New Password"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password")], "Passwords do not match")
        .required("Please Confirm The New Password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setPasswordSaving(true);
      setPasswordAlert(null);
      try {
        const res = await changeUserPassword(values.current_password, values.new_password);
        if (res && res.success) {
          setPasswordAlert({ color: "success", message: hasPassword ? "Password changed." : "Password set. You can now log in with email & password." });
          resetForm();
          loadUser();
        } else {
          setPasswordAlert({ color: "danger", message: (res && res.message) || "Failed to update password." });
        }
      } catch (err) {
        setPasswordAlert({ color: "danger", message: "Failed to update password." });
      } finally {
        setPasswordSaving(false);
      }
    },
  });

  const avatarSrc = currentUser && currentUser.avatar ? currentUser.avatar : defaultAvatar;
  const fullName = currentUser
    ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() || "User"
    : "";

  return (
    <React.Fragment>
      <div className="page-content mt-lg-5">
        <Container fluid>
          <Row>
            <Col lg="12">
              {profileAlert ? <Alert color={profileAlert.color}>{profileAlert.message}</Alert> : null}

              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="mx-3 position-relative">
                      <img
                        src={avatarSrc}
                        alt=""
                        className="avatar-lg rounded-circle img-thumbnail"
                        style={{ objectFit: "cover" }}
                      />
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={onAvatarSelected}
                      />
                      <Button
                        color="primary"
                        size="sm"
                        className="rounded-circle position-absolute"
                        style={{ bottom: 0, right: 0, width: 32, height: 32 }}
                        title="Change avatar"
                        disabled={avatarUploading}
                        onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
                      >
                        {avatarUploading ? <Spinner size="sm" /> : <i className="ri-camera-fill" />}
                      </Button>
                    </div>
                    <div className="flex-grow-1 align-self-center ms-2">
                      <div className="text-muted">
                        <h5>{fullName}</h5>
                        <p className="mb-1">Email : {currentUser ? currentUser.email : ""}</p>
                        <p className="mb-0">
                          Credit:{" "}
                          <b>
                            {currentUser
                              ? ((currentUser.sub_credit_remain || 0) + (currentUser.bought_credit || 0)).toLocaleString("en-US")
                              : 0}
                          </b>
                        </p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Profile Info</h5>
                </CardHeader>
                <CardBody>
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      profileValidation.handleSubmit();
                      return false;
                    }}
                  >
                    <div className="mb-3">
                      <Label className="form-label">First Name</Label>
                      <Input
                        name="first_name"
                        className="form-control"
                        placeholder="Enter First Name"
                        type="text"
                        onChange={profileValidation.handleChange}
                        onBlur={profileValidation.handleBlur}
                        value={profileValidation.values.first_name || ""}
                        invalid={profileValidation.touched.first_name && profileValidation.errors.first_name ? true : false}
                      />
                      {profileValidation.touched.first_name && profileValidation.errors.first_name ? (
                        <FormFeedback type="invalid">{profileValidation.errors.first_name}</FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">Last Name</Label>
                      <Input
                        name="last_name"
                        className="form-control"
                        placeholder="Enter Last Name"
                        type="text"
                        onChange={profileValidation.handleChange}
                        onBlur={profileValidation.handleBlur}
                        value={profileValidation.values.last_name || ""}
                      />
                    </div>
                    <div className="text-end">
                      <Button type="submit" color="primary" disabled={profileSaving}>
                        {profileSaving ? <Spinner size="sm" className="me-2" /> : null}
                        Update Profile
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            <Col lg="6">
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{hasPassword ? "Change Password" : "Set Password"}</h5>
                </CardHeader>
                <CardBody>
                  {passwordAlert ? <Alert color={passwordAlert.color}>{passwordAlert.message}</Alert> : null}
                  {!hasPassword && currentUser ? (
                    <p className="text-muted">
                      Your account was created with Google sign-in and has no password yet. Set one to also log in with email &amp; password.
                    </p>
                  ) : null}
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      passwordValidation.handleSubmit();
                      return false;
                    }}
                  >
                    {hasPassword ? (
                      <div className="mb-3">
                        <Label className="form-label">Current Password</Label>
                        <Input
                          name="current_password"
                          className="form-control"
                          placeholder="Enter Current Password"
                          type="password"
                          autoComplete="current-password"
                          onChange={passwordValidation.handleChange}
                          onBlur={passwordValidation.handleBlur}
                          value={passwordValidation.values.current_password || ""}
                          invalid={passwordValidation.touched.current_password && passwordValidation.errors.current_password ? true : false}
                        />
                        {passwordValidation.touched.current_password && passwordValidation.errors.current_password ? (
                          <FormFeedback type="invalid">{passwordValidation.errors.current_password}</FormFeedback>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="mb-3">
                      <Label className="form-label">New Password</Label>
                      <Input
                        name="new_password"
                        className="form-control"
                        placeholder="Enter New Password"
                        type="password"
                        autoComplete="new-password"
                        onChange={passwordValidation.handleChange}
                        onBlur={passwordValidation.handleBlur}
                        value={passwordValidation.values.new_password || ""}
                        invalid={passwordValidation.touched.new_password && passwordValidation.errors.new_password ? true : false}
                      />
                      {passwordValidation.touched.new_password && passwordValidation.errors.new_password ? (
                        <FormFeedback type="invalid">{passwordValidation.errors.new_password}</FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">Confirm New Password</Label>
                      <Input
                        name="confirm_password"
                        className="form-control"
                        placeholder="Confirm New Password"
                        type="password"
                        autoComplete="new-password"
                        onChange={passwordValidation.handleChange}
                        onBlur={passwordValidation.handleBlur}
                        value={passwordValidation.values.confirm_password || ""}
                        invalid={passwordValidation.touched.confirm_password && passwordValidation.errors.confirm_password ? true : false}
                      />
                      {passwordValidation.touched.confirm_password && passwordValidation.errors.confirm_password ? (
                        <FormFeedback type="invalid">{passwordValidation.errors.confirm_password}</FormFeedback>
                      ) : null}
                    </div>
                    <div className="text-end">
                      <Button type="submit" color="primary" disabled={passwordSaving}>
                        {passwordSaving ? <Spinner size="sm" className="me-2" /> : null}
                        {hasPassword ? "Change Password" : "Set Password"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
